import { Sentry } from "@/utils/sentryUtils";
import { DateTime } from "luxon";

type USBCommand<T extends unknown[]> = {
  type: number;
  code: number;
  payload: (...args: T) => number[];
};

type FileInfo = {
  handle: number;
  name: string;
  fileType: "directory" | "file" | "unknown";
};

type USBResponse = {
  status: USBTransferStatus;
  length: number;
  type: string | undefined;
  code: string | undefined;
  transactionID: number;
  payload: ArrayBuffer;
  parameters: number[];
};

type USBConfig = {
  interface: USBInterface;
  outEndpoint: number;
  outPacketSize: number;
  inEndpoint: number;
  inPacketSize: number;
};

const PacketType = [undefined, "Command", "Data", "Response", "Event"];

const USBCommands = {
  OPEN_SESSION: {
    type: 1,
    code: 0x1002,
    payload: () => [1],
  },
  CLOSE_SESSION: {
    type: 1,
    code: 0x1003,
    payload: () => [0],
  },
  GET_OBJECT_HANDLES: {
    type: 1,
    code: 0x1007,
    payload: () => [0xffffffff, 0, 0xffffffff],
  },
  GET_OBJECT_INFO: {
    type: 1,
    code: 0x1008,
    payload: (handle: number) => [handle],
  },
  GET_FILE_NAME: {
    type: 1,
    code: 0x9803,
    payload: (handle: number) => [handle, 0xdc07],
  },
  GET_OBJECT: {
    type: 1,
    code: 0x1009,
    payload: (handle: number) => [handle],
  },
} satisfies Record<string, USBCommand<never[]>>;

const ObjectInfoData = {
  PARENT_HANDLE: 38,
  ASSOCIATION_TYPE: 42,
  FILE_NAME: 52,
};

export class MediaTransferProtocol {
  private config: USBConfig | undefined;
  private transactionID = 1;

  constructor(private readonly device: USBDevice) {}

  openSession = async () => {
    Sentry.addBreadcrumb({
      type: "default",
      category: "MTP",
      message:"Starting to open session",
      timestamp: DateTime.now().toMillis(),
    });
    console.debug("Opening session");
    await this._writeCommand(USBCommands.OPEN_SESSION);
    await this._readResponse();
  };

  getObjectHandles = async () => {
    Sentry.addBreadcrumb({
      type: "default",
      category: "MTP",
      message:"Getting object handles",
      timestamp: DateTime.now().toMillis(),
    });
    console.debug("Getting object handles");
    await this._writeCommand(USBCommands.GET_OBJECT_HANDLES);
    const response = await this._readData();
    return response.parameters;
  };

  getFileInfo = async (handles: number[]): Promise<Record<string, FileInfo>> => {
    Sentry.addBreadcrumb({
      type: "default",
      category: "MTP",
      message:"Getting file info",
      timestamp: DateTime.now().toMillis(),
    });
    console.debug("Getting file info");
    const result: Record<string, FileInfo> = {};
    for (const handle of handles) {
      await this._writeCommand(USBCommands.GET_OBJECT_INFO, handle);
      const fileInfoResult = await this._readData();
      const payload = new DataView(fileInfoResult.payload);
      const fileName = this._getString(payload, ObjectInfoData.FILE_NAME);
      const associationType = payload.getUint16(ObjectInfoData.ASSOCIATION_TYPE, true);
      const fileType: FileInfo["fileType"] =
        associationType === 0x0001 ? "directory" : associationType === 0x0000 ? "unknown" : "file";

      result[fileName] = {
        handle,
        name: fileName,
        fileType,
      };
    }
    return result;
  };

  getFile = async (handle: number) => {
    Sentry.addBreadcrumb({
      type: "default",
      category: "MTP",
      message:"Getting file",
      timestamp: DateTime.now().toMillis(),
    });
    console.debug("[MTP] Getting file");
    await this._writeCommand(USBCommands.GET_OBJECT, handle);
    const fileResult = await this._readData();
    return fileResult.payload;
  };

  closeSession = async () => {
    Sentry.addBreadcrumb({
      type: "default",
      category: "MTP",
      message:"Closing session",
      timestamp: DateTime.now().toMillis(),
    });
    console.debug("[MTP] Closing session");
    try {
      await this._writeCommand(USBCommands.CLOSE_SESSION);
      await this._readResponse();
    } catch (e) {
      Sentry.captureException(e);
      console.debug("Failed to close session", e);
    }
  };

  connect = async () => {
    if (this.device.opened) {
      Sentry.addBreadcrumb({
        type: "default",
        category: "MTP",
        message:"Device already opened, closing",
        timestamp: DateTime.now().toMillis(),
      });
      await this.device.close();
    }
    await this.device.open();
    Sentry.addBreadcrumb({
      type: "default",
      category: "MTP",
      message:"Connect - opened USB device",
      data: {
        vendorId: this.device.vendorId,
        productId: this.device.productId,
      },
      timestamp: DateTime.now().toMillis(),
    });
    await this.device.selectConfiguration(1);
    const interfaces = this.device.configuration?.interfaces ?? [];
    if (interfaces.length === 0) {
      const error = new Error("[MTP] Connect - No interfaces found");
      Sentry.captureException(error);
      throw error;
    }
    let claimedInterface = undefined;
    for (const usbInterface of interfaces) {
      try {
        Sentry.addBreadcrumb({
          type: "default",
          category: "MTP",
          message:"Connect - Trying to claim interface",
          data: {
            interfaceNumber: usbInterface.interfaceNumber,
          },
          timestamp: DateTime.now().toMillis(),
        });
        await this.device.claimInterface(usbInterface.interfaceNumber);
        claimedInterface = usbInterface;
        break;
      } catch (e) {
        Sentry.captureException(e);
        console.debug("Failed to claim interface", e);
      }
    }
    if (!claimedInterface) {
      const error = new Error("[MTP] Connect - Could not claim interface");
      Sentry.captureException(error);
      throw error;
    }
    const endpointOut = claimedInterface.alternate.endpoints.find(ep => ep.direction === "out");
    const endpointIn = claimedInterface.alternate.endpoints.find(ep => ep.direction === "in");
    if (!endpointOut || !endpointIn) {
      const error = new Error("[MTP] Connect - Could not find endpoints");
      Sentry.captureException(error);
      throw error;
    }
    Sentry.addBreadcrumb({
      type: "default",
      category: "MTP",
      message:"Claimed interface",
      data: {
        interfaceNumber: claimedInterface.interfaceNumber,
      },
      timestamp: DateTime.now().toMillis(),
    });
    this.config = {
      interface: claimedInterface,
      outEndpoint: endpointOut.endpointNumber,
      outPacketSize: endpointOut.packetSize || 1024,
      inEndpoint: endpointIn.endpointNumber,
      inPacketSize: endpointIn.packetSize || 1024,
    };

    return this.config;
  };

  disconnect = async () => {
    await this.device.close();
    Sentry.addBreadcrumb({
      type: "default",
      category: "MTP",
      message:"Closed USB device",
      data: {
        serialNumber: this.device.serialNumber,
      },
      timestamp: DateTime.now().toMillis(),
    });
    this.config = undefined;
    this.transactionID = 1;
  };

  _buildCommandPacket = <T extends unknown[]>(command: USBCommand<T>, ...args: T) => {
    const payload = command.payload(...args);
    // payload parameters are always 4 bytes in length
    const packetLength = 12 + payload.length * 4;

    const buf = new ArrayBuffer(packetLength);
    const bytes = new DataView(buf);
    bytes.setUint32(0, packetLength, true);
    bytes.setUint16(4, command.type, true);
    bytes.setUint16(6, command.code, true);
    bytes.setUint32(8, this.transactionID, true);

    payload.forEach((element, index) => {
      bytes.setUint32(12 + index * 4, element, true);
    });
    this.transactionID += 1;
    return buf;
  };

  _writeCommand = async <T extends unknown[]>(command: USBCommand<T>, ...args: T) => {
    if (!this.config) {
      const error = new Error("[MTP] Write - No configuration found");
      Sentry.captureException(error);
      throw error;
    }
    const buffer = this._buildCommandPacket<T>(command, ...args);
    const commandResult = await this.device.transferOut(this.config?.outEndpoint, buffer);
    if (commandResult.status !== "ok") {
      const error = new Error("[MTP] Write - Couldn't write command");
      Sentry.captureException(error);
      throw error;
    }
  };

  _parseResponse(status: USBTransferStatus, bytes: DataView<ArrayBuffer>) {
    const length = bytes.getUint32(0, true);
    const typeValue = bytes.getUint16(4, true);
    const codeValue = bytes.getUint16(6, true);
    const transactionID = bytes.getUint32(8, true);
    const payload = bytes.buffer.slice(12);
    const response: USBResponse = {
      status,
      length,
      type: PacketType[typeValue],
      code: Object.entries(USBCommands).find(([_, value]) => value.code === codeValue)?.[0],
      transactionID,
      payload,
      parameters: [] as number[],
    };

    for (let i = 12; i < length; i += 4) {
      if (i <= length - 4) {
        response.parameters.push(bytes.getUint32(i, true));
      }
    }

    return response;
  }

  async _readResponse() {
    if (!this.config) {
      const error = new Error("[MTP] Read - No configuration found");
      Sentry.captureException(error);
      throw error;
    }
    let result = await this.device.transferIn(this.config.inEndpoint, this.config.inPacketSize);
    const data = result?.data as DataView<ArrayBuffer> | undefined;
    const status = result?.status;
    if (!data || data.byteLength === 0 || !status) {
      throw new Error("No data found");
    }

    const containerLength = data.getUint32(0, true);
    const raw = new Uint8Array(containerLength);
    raw.set(new Uint8Array(data.buffer), 0);

    let offset = data.byteLength;
    while (offset < containerLength) {
      result = await this.device.transferIn(this.config.inEndpoint, this.config.inPacketSize);
      if (result.data) {
        raw.set(new Uint8Array(result.data.buffer), offset);
        offset += result.data.byteLength;
      }
    }

    return this._parseResponse(status, new DataView(raw.buffer));
  }

  async _readData() {
    const result = await this._readResponse();
    const response = await this._readResponse();
    if (result.type !== "Data") {
      throw new Error("No data found");
    }
    if (response.type !== "Response") {
      throw new Error("No response found");
    }

    return result;
  }

  _getString = (data: DataView<ArrayBuffer>, offset: number) => {
    const size = data.getInt8(offset);
    const fileNameBuffer = data.buffer.slice(
      offset + 1,
      // Size - 1 for trailing 0
      offset + 1 + (size - 1) * 2,
    );
    return new TextDecoder("utf-16le").decode(fileNameBuffer);
  };
}
